import * as React from 'react'
import Dialog from '@material-ui/core/Dialog'
import { useAtom } from 'jotai'
import styled from 'styled-components'
import { X } from '@geist-ui/react-icons'
import { crossmintSecondaryAtom, SecondaryAction } from '../../atoms/Wallet'
import { useMediaQuery } from '@material-ui/core'
import log from '../../events/log'
import { ClickExportFromCrossmint } from '../../events/revenue_events'

// ========================== React component ==================================

const CrossmintTransferDialog = (): JSX.Element => {
  //   const [, setToast] = useToasts()
  const isMobile = useMediaQuery('(max-width: 43em)')
  const [secondaryInfo, setOpen] = useAtom(crossmintSecondaryAtom)

  return (
    <Dialog
      onClose={() => {
        console.log('Closed')
      }}
      aria-labelledby="crossmint-dialog"
      open={secondaryInfo !== undefined}
      PaperProps={{
        style: {
          backgroundColor: '#101010',
          borderRadius: 15,
          minWidth: isMobile ? '90vw' : '500px',
          width: isMobile ? '90vw' : '60vh',
          borderWidth: 1,
          borderColor: '#262626',
          borderStyle: 'solid',
          minHeight: isMobile ? '400px' : '450px',
          height: '45vh',
        },
      }}
      BackdropProps={{
        style: {
          backdropFilter: 'blur(20px)',
        },
      }}
    >
      <OuterDiv>
        <CloseButton color="white" size={'30px'} onClick={() => setOpen(undefined)} />
        <Title>
          {secondaryInfo?.type === SecondaryAction.LISTING
            ? 'Selling your Glass NFT'
            : secondaryInfo?.type === SecondaryAction.OFFER
            ? 'Making an Offer'
            : 'Selling your Glass NFT'}
        </Title>
        <Subtitle>
          {`You'll need to export your NFT from Crossmint to your own Solana wallet to use the Glass marketplace.`}
        </Subtitle>
        {secondaryInfo?.type === SecondaryAction.OFFER ? (
          <Steps>
            <StepDiv>
              <StepNumber>1</StepNumber>
              <TextDiv>
                <Text>Get a </Text>
                <Link href="https://phantom.app/" target="_blank">
                  Phantom
                </Link>
                <Text> wallet. Copy your new wallet address.</Text>
              </TextDiv>
            </StepDiv>
            <StepDiv>
              <StepNumber>2</StepNumber>
              <TextDiv>
                <Text>Transfer SOL to your wallet using an exchange like </Text>
                <Link
                  href="https://glass.helpkit.so/getting-started/eJk11yw5RUHAhqFpxpZrev/how-do-i-add-money-to-my-wallet/8k7Mw9cMxe7RcKiRkYtnht"
                  target="_blank"
                >
                  Coinbase
                </Link>
                <Text>.</Text>
              </TextDiv>
            </StepDiv>
            <StepDiv>
              <StepNumber>3</StepNumber>
              <TextDiv>
                <Text>{`Click your profile in the header and disconnect your Crossmint wallet from Glass from the dropdown menu.`}</Text>
              </TextDiv>
            </StepDiv>
            <StepDiv>
              <StepNumber>4</StepNumber>
              <TextDiv>
                <Text>{`Reconnect to Glass with your Phantom wallet. Click ‘Make Offer’ again and you’ll be able to make an offer!`}</Text>
              </TextDiv>
            </StepDiv>
          </Steps>
        ) : (
          <Steps>
            <StepDiv>
              <StepNumber>1</StepNumber>
              <TextDiv>
                <Text>Get a </Text>
                <Link href="https://phantom.app/" target="_blank">
                  Phantom
                </Link>
                <Text> wallet. Copy your new wallet address.</Text>
              </TextDiv>
            </StepDiv>
            <StepDiv>
              <StepNumber>2</StepNumber>
              <TextDiv>
                <Text>{`Click the 'Export' button `}</Text>
                <Link
                  href={`https://${
                    process.env.NEXT_PUBLIC_ENVIRONMENT === 'production' ? '' : 'staging.'
                  }crossmint.io/user/collection/sol:${secondaryInfo?.video.address}`}
                  target="_blank"
                  onClick={() => {
                    log(new ClickExportFromCrossmint(secondaryInfo?.video.address ?? ''))
                  }}
                >
                  here
                </Link>
                <Text> and enter your new address.</Text>
              </TextDiv>
            </StepDiv>
            <StepDiv>
              <StepNumber>3</StepNumber>
              <TextDiv>
                <Text>{`Click your profile in the header and disconnect your Crossmint wallet from Glass from the dropdown menu.`}</Text>
              </TextDiv>
            </StepDiv>
            <StepDiv>
              <StepNumber>4</StepNumber>
              <TextDiv>
                <Text>{`Reconnect to Glass with your Phantom wallet. Click the ‘Sell’ button and you’ll be able to sell your NFT!`}</Text>
              </TextDiv>
            </StepDiv>
          </Steps>
        )}
      </OuterDiv>
    </Dialog>
  )
}

export default CrossmintTransferDialog

// ========================== Styled components ================================

const OuterDiv = styled.div`
  width: calc(100% - 50px);
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  margin: 25px;
  margin-top: 0px;
`

const Title = styled.div`
  color: white;
  font-size: 28px;
  font-weight: 600;
  text-align: center;
  padding: 25px 0 0 0;

  @media (max-width: 43em) {
    font-size: 20px;
  }
`

const Subtitle = styled.div`
  color: #aaa;
  font-size: 16px;
  font-weight: 500;
  text-align: left;

  @media (max-width: 43em) {
    font-size: 14px;
  }
`

const CloseButton = styled(X)`
  right: 4%;
  top: 4%;
  position: absolute;
  cursor: pointer;
`

const Steps = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const StepDiv = styled.div`
  width: calc(100% - 30px);
  padding: 15px;
  border-radius: 10px;
  background-color: #262626;
  color: white;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
`
const TextDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
`

const Text = styled.span`
  font-size: 15px;
  font-weight: 500;
  white-space: pre-wrap;
  text-overflow: wrap;

  @media (max-width: 43em) {
    font-size: 12px;
  }
`

const StepNumber = styled.span`
  font-size: 15px;
  font-weight: 600;
  margin-right: 25px;

  @media (max-width: 43em) {
    font-size: 12px;
  }
`

const Link = styled.a`
  color: #aaa;
  font-size: 15px;
  font-weight: 500;

  @media (max-width: 43em) {
    font-size: 12px;
  }
`
